import React, {useRef, useState} from 'react';
import Card from '../common/Card/Card';
import Input from '../common/Input/Input';
import Textarea from '../common/Textarea/Textarea';
import styles from './QuestionRenderer.module.css';

import { post } from '../../services/api';
import {FiCalendar, FiChevronDown, FiClock, FiStar} from "react-icons/fi";
import {RATING_ICONS} from "../FormBuilder/QuestionTypeSpecificEditors/icon-pool";
import {useIntl} from "react-intl"; // Импорт для загрузки файлов

const QuestionRenderer = ({ question, answer, onAnswerChange, error }) => {
    const { id, text, is_required, type, options } = question;
    const intl = useIntl();
    const handleInputChange = (e) => {
        onAnswerChange(id, e.target.value);
    };

    const handleCheckboxChange = (optionId) => {
        const currentAnswers = answer ? [...answer] : [];
        const optionIndex = currentAnswers.indexOf(optionId);

        if (optionIndex > -1) {
            currentAnswers.splice(optionIndex, 1); // Убрать, если уже выбран
        } else {
            currentAnswers.push(optionId); // Добавить, если не выбран
        }
        onAnswerChange(id, currentAnswers);
    };

    const renderInput = () => {

        const isInvalid = !!error;

        switch (type) {
            // QUESTION_TYPES.TEXT
            case 'text':
                return <Input type="text" value={answer || ''} onChange={handleInputChange} required={is_required} isInvalid={isInvalid} />;
            case 'paragraph':
                return <Textarea value={answer || ''} onChange={handleInputChange} required={is_required} isInvalid={isInvalid} />;
            case 'date':
                return (
                    <div className={styles.inputWithIconWrapper}>
                        <Input type="date" value={answer || ''} onChange={handleInputChange} required={is_required}
                               isInvalid={isInvalid}/>
                        <FiCalendar className={styles.inputIcon}/>
                    </div>
                )
            // return <Input type="date" value={answer || ''} onChange={handleInputChange} required={is_required} isInvalid={isInvalid} />;
            case 'time':
                return (
                    <div className={styles.inputWithIconWrapper}>
                        <Input type="time" value={answer || ''} onChange={handleInputChange} required={is_required} isInvalid={isInvalid} />
                        <FiClock className={styles.inputIcon} />
                    </div>
                );
                // return <Input type="time" value={answer || ''} onChange={handleInputChange} required={is_required} isInvalid={isInvalid} />;
            case 'dropdown':
                return (
                    <div className={styles.selectWrapper}>
                        <select className={`${styles.select} ${isInvalid ? styles.invalid : ''}`} value={answer || ''} onChange={handleInputChange} required={is_required}>
                            <option value="" disabled>{/* Выберите вариант */}</option>
                            {options.map(opt => (
                                <option key={opt.id} value={opt.id}>{opt.text}</option>
                            ))}
                        </select>
                        <FiChevronDown className={styles.selectIcon} />
                    </div>
                );
            case 'radio':
                return (
                    <div className={styles.optionsContainer}>
                        {options.map(opt => (
                            <label key={opt.id} className={styles.optionLabel}>
                                <input type="radio" name={id} value={opt.id} checked={answer === opt.id} onChange={(e) => onAnswerChange(id, Number(e.target.value) || e.target.value)} required={is_required} />
                                <span className={styles.optionText}>{opt.text}</span>
                            </label>
                        ))}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className={styles.optionsContainer}>
                        {options.map(opt => (
                            <label key={opt.id} className={styles.optionLabel}>
                                <input type="checkbox" value={opt.id} checked={(answer || []).includes(opt.id)} onChange={() => handleCheckboxChange(opt.id)} />
                                <span className={styles.optionText}>{opt.text}</span>
                            </label>
                        ))}
                    </div>
                );
            case 'rating':
                const iconKey = question.config?.icon || 'star';
                const IconComponent = RATING_ICONS[iconKey]?.Icon || FiStar;
                const count = question.options?.length || 5;

                const firstLabel = question.options?.[0]?.text;
                const lastLabel = question.options?.[count - 1]?.text;

                return (
                    <div className={styles.ratingContainer}>
                        {Array.from({ length: count }, (_, i) => i + 1).map(value => (
                            <IconComponent
                                key={value}
                                className={`${styles.ratingIcon} ${answer >= value ? styles.filled : ''}`}
                                onClick={() => onAnswerChange(id, value)}
                                // --- ИЗМЕНЕНИЕ: Читаем метку из option.label ---
                                title={question.options[value - 1]?.text || `${value}`}
                            />
                        ))}
                    </div>
                );
            case 'linear_scale':
                const min = question.config?.min ?? 1;
                const max = question.config?.max ?? 5;
                const minLabel = question.config?.min_label;
                const maxLabel = question.config?.max_label;
                // Создаем массив чисел от min до max
                const scaleValues = Array.from({ length: max - min + 1 }, (_, i) => min + i);

                return (
                    <div className={styles.scaleContainer}>
                        {minLabel && <span className={styles.scaleEndLabel}>{minLabel}</span>}
                        <div className={styles.scaleValues}>
                            {scaleValues.map(value => (
                                <label key={value} className={styles.scaleLabel}>
                                    <input type="radio" name={id} value={value} checked={answer == value} onChange={handleInputChange} />
                                    <span className={styles.scaleNumber}>{value}</span>
                                </label>
                            ))}
                        </div>
                        {maxLabel && <span className={styles.scaleEndLabel}>{maxLabel}</span>}
                    </div>
                );
            case 'file':
                return <FileUploader questionId={id} onAnswerChange={onAnswerChange} />;
            case 'phone':
                return (
                    <Input
                        type="tel" // 'tel' семантически корректен и вызывает цифровую клавиатуру на мобильных
                        value={answer || ''}
                        onChange={handleInputChange} // handleInputChange просто вызывает onAnswerChange
                        required={is_required}
                        placeholder={intl.formatMessage({ id: "phoneInput.placeholder" })}
                        isInvalid={isInvalid}
                    />
                );
            case 'email':
                return (
                    <Input
                        type="email" // <--- Важный семантический тип
                        value={answer || ''}
                        onChange={handleInputChange}
                        required={is_required}
                        isInvalid={isInvalid} // Для подсветки ошибки
                        placeholder="example@email.com"
                        // Включаем проверку орфографии, т.к. в email могут быть имена
                        spellCheck="false"
                        // Включаем автозаполнение из браузера
                        autoComplete="email"
                    />
                );
            case 'integer':
                return (
                    <Input
                        type="number"
                        step="1" // Разрешает только целые числа в нативных контролах браузера
                        value={answer || ''}
                        onChange={handleInputChange}
                        required={is_required}
                        isInvalid={isInvalid}
                    />
                );

            // --- НОВЫЙ CASE ДЛЯ ДРОБНОГО ЧИСЛА ---
            case 'decimal':
                return (
                    <Input
                        type="number"
                        step="any" // Разрешает любые дробные числа
                        value={answer || ''}
                        onChange={handleInputChange}
                        required={is_required}
                        isInvalid={isInvalid}
                    />
                );
            case 'yes_no':
                return (
                    <div className={styles.optionsContainer}>
                        <label className={styles.optionLabel}>
                            <input
                                type="radio"
                                name={id}
                                value="true"
                                checked={answer === true} // Строгое сравнение с boolean
                                onChange={() => onAnswerChange(id, true)}
                                required={is_required}
                            />
                            <span className={styles.optionText}>{intl.formatMessage({ id: "common.yes" })}</span>
                        </label>
                        <label className={styles.optionLabel}>
                            <input
                                type="radio"
                                name={id}
                                value="false"
                                checked={answer === false} // Строгое сравнение с boolean
                                onChange={() => onAnswerChange(id, false)}
                                required={is_required}
                            />
                            <span className={styles.optionText}>{intl.formatMessage({ id: "common.no" })}</span>
                        </label>
                    </div>
                );
            default:
                return <p>Неизвестный тип вопроса.</p>;
        }
    };

    return (
        <Card className={`${styles.questionCard} ${error ? styles.errorState : ''}`}>
            <label className={styles.questionLabel}>
                {text}
                {is_required && <span className={styles.requiredMark}>*</span>}
            </label>
            {renderInput()}
            {error && <p className={styles.errorMessage}>{error}</p>}
        </Card>
    );
};

export default QuestionRenderer;

const FileUploader = ({ questionId, onAnswerChange }) => {
    const intl = useIntl();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [fileName, setFileName] = useState('');
    const inputRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setError('');
        setUploading(true);
        setFileName(file.name);

        const formData = new FormData();
        formData.append('file', file);

        try {
            // const response = await post('files/upload', formData, {
            //     headers: { 'Content-Type': 'multipart/form-data' }
            // });
            const response = await post('files/upload', formData); // Стало
            onAnswerChange(questionId, response.file_id);
        } catch (err) {
            setError(err.response?.data?.message || 'Upload failed.');
            onAnswerChange(questionId, null); // Сбрасываем ответ в случае ошибки
            setFileName('');
            // Программно сбрасываем сам input, чтобы можно было выбрать тот же файл снова
            if (inputRef.current) {
                inputRef.current.value = null;
            }
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className={styles.fileUploader}>
            <input type="file" id={`file-${questionId}`} ref={inputRef} onChange={handleFileChange} disabled={uploading} />
            <label htmlFor={`file-${questionId}`} className={styles.fileLabel}>
                {uploading
                    // --- ИЗМЕНЕНИЕ ЗДЕСЬ ---
                    ? intl.formatMessage({ id: "fileUploader.uploading" })
                    : (fileName || intl.formatMessage({ id: "fileUploader.selectFile" }))
                }
            </label>
            {error && <p className={styles.uploadError}>{error}</p>}
        </div>
    );
};
